import { Child } from 'pym.js';
import * as d3 from 'd3';
import meta from './data/meta.json';
import fires from './data/filtered.geojson';
import labels from './data/filtered-points.geojson';

import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils
import { stylePremium } from './utils/helpers';

// styles
import './styles/graphic.scss';

let latestData;

const mapboxgl = require('mapbox-gl');

const pkg = require('../package.json');

const ai2html = null;

if (process.env.NODE_ENV === 'development') {
  // turning ai2html's output into a module allows
  // webpack to apply hot module replacement.
  // we obviously want to bake it into the html in production
  // that's why we're conditionally requiring it
  // ai2html = require('./ai2html-graphic.html');
}

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ renderCallback: draw, id: pkg.name });

console.clear();

window.onload = () => {
  // necessary evil for right now
  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;

stylePremium(CNNPolitics, CNNBusiness);

let chartData;
let rawData;
let lastUpdated;
let subheadText;

function draw() {
  if (process.env.NODE_ENV === 'development') {
  }
}

mapboxgl.accessToken = 'pk.eyJ1IjoiY25uZGlnaXRhbCIsImEiOiJiYjIwMTgxNDZmNjgyODllMjAxYTYwOTAxYmY2MzI2MyJ9.8F6TQITXymFzCXy8tBqGgg';

const MAP_ID = 'mapbox://styles/cnndigital/cl960ytee001215qu1uhorjlx';
const LAYER_OPACITY = 1;

let mapParams;
let hasLocation;
const usingParentDotHtml = false;
let outageEvent;

const mapBounds = [
  [-114, 37], // Southwest corner: [longitude, latitude]
  [-110, 41], // Northeast corner: [longitude, latitude]
];


// screen size config
const optionsSm = {
  initZoom: 5.3,
  minZoom: 5.3,
  maxZoom: 11,
  center: [-112, 39.4],
  mapBounds,
};


const optionsLg = {
  initZoom: 6,
  minZoom: 6,
  maxZoom: 12,
  center: [-112.10936209999409, 39.22517831187963],
  mapBounds,
};


const drawMap = () => {
  const container = d3.select('#map');
  container.html('');

  const mq = window.matchMedia('(min-width: 640px)');
  const isDesktop = window.matchMedia('(min-width: 640px)').matches;

  let options;

  const updateOptions = (opts, isSmall) => {
    if (mapParams && mapParams.lnglat) {
      opts.center = mapParams.lnglat;
      opts.initZoom = (isSmall) ? mapParams.z - 0.75 : mapParams.z;
    }
    return opts;
  };

  const handleBrowserSize = () => {
    if (isDesktop) {
      d3.select('#map').attr('style', 'width: 100%; height: 575px;');
      options = optionsLg;
      options = updateOptions(optionsLg, false);
    } else {
      d3.select('#map').attr('style', 'width: 100%; height: 375px;');
      options = optionsSm;
      options = updateOptions(optionsSm, true);
    }
  };
  handleBrowserSize();

  const map = new mapboxgl.Map({
    container: 'map',
    style: MAP_ID,
    center: options.center,
    zoom: options.initZoom,
    maxZoom: options.maxZoom,
    minZoom: options.minZoom,
    bearing: 0,
    scrollZoom: true,
    dragRotate: false,
    logoPosition: 'bottom-left',
    touchPitch: false,
    interactive: true,
    projection: 'albers',
    maxBounds: options.mapBounds,
  });

  // disable map rotation using right click + drag
  map.dragRotate.disable();

  // disable map rotation using touch rotation gesture
  map.touchZoomRotate.disableRotation();

  const nav = new mapboxgl.NavigationControl({
    showCompass: false,
  });
  map.addControl(nav, 'top-right');

  map.on('load', () => {
  // Add a new GeoJSON source using a public URL
    map.addSource('fire-source', {
      type: 'geojson',
      data: fires,
    });

    // Add a layer to visualize the GeoJSON data
    map.addLayer({
      id: 'fire-perimeter-layer',
      type: 'fill',
      source: 'fire-source',
      paint: {
        'fill-color': '#BA0017',
        'fill-opacity': 0.4,
      },
      filter: ['all',
        ['any',
          ['==', ['get', 'attr_PercentContained'], null],
          ['<', ['get', 'attr_PercentContained'], 100],
        ],
        // ['>', ['get', 'attr_IncidentSize'], min_acre_threshold],
        ['==', ['get', 'attr_POOState'], 'US-UT'],
      ],
    });

    map.addSource('local-geojson', {
      type: 'geojson',
      data: labels,
    });


    //
    const desktopX = 0.25;
    const desktopY = -2.5;

    const mobileX = 0.25;
    const mobileY = -2.4;

    const min_acre_threshold = 500;

    // Add the text label layer
    map.addLayer({
      id: 'fire-label-layer',
      type: 'symbol',
      source: 'local-geojson',
      layout: {
        'text-field': ['concat', ['get', 'attr_IncidentName'], ' fire'], // Use the "Name" property for labels
        'text-font': ['CNN Sans Display Bold', 'Arial Unicode MS Bold'],
        'text-size': isDesktop ? 12 : 11.5,
        // 'text-offset': isDesktop ? [desktopX, -1 + desktopY] : [mobileX, -1 + mobileY],
        'text-offset': [
          'match',
          ['get', 'attr_IncidentName'],
          'Cherry', ['literal', (isDesktop ? [desktopX, 2 + desktopY] : [mobileX, 2 + mobileY])],
          'Kane Springs', ['literal', (isDesktop ? [desktopX, 3 + desktopY] : [mobileX, 3 + mobileY])],
          'Sawmill', ['literal', (isDesktop ? [desktopX, 3.5 + desktopY] : [mobileX, 3.5 + mobileY])],
          ['literal', (isDesktop ? [desktopX, -1 + desktopY] : [mobileX, -1 + mobileY])], // Default value: If no match
        ],

        'text-anchor': [
          'match',
          ['get', 'attr_IncidentName'],
          'Iron', 'left',
          'Tower', 'right',
          'Hastings', 'right',
          'Sawmill', isDesktop ? 'right' : 'center',
          'Kane Springs', isDesktop ? 'right' : 'right',
          'Cherry', 'right',
          'Maple Peak', 'top-left',
          'Grapevine', isDesktop ? 'left' : 'right',
          'left', // Default value: If no match
        ],
        'text-allow-overlap': true,
      },
      paint: {
        'text-halo-color': '#FFFFFF', // White halo
        'text-halo-width': 1,
        'text-halo-blur': 0.5,
        'text-color': [
          'match',
          ['get', 'active'],
          'true', '#000000',
          'false', '#666666',
          '#000000', // Default value: If no match
        ],
      },
      filter: ['all',
        ['any',
          ['==', ['get', 'attr_PercentContained'], null],
          ['<', ['get', 'attr_PercentContained'], 100],
        ],
        ['>', ['get', 'attr_IncidentSize'], min_acre_threshold],
        ['==', ['get', 'attr_POOState'], 'US-UT'],
      ],
    });


    map.addLayer({
      id: 'fire-meta-layer',
      type: 'symbol',
      source: 'local-geojson',
      layout: {
        'text-field': ['concat',
          ['number-format', ['round', ['to-number', ['get', 'attr_IncidentSize'], 0]], { 'min-fraction-digits': 0, 'max-fraction-digits': 0 }],
          ' acres burned',
        ],
        'text-font': ['CNN Sans Display Regular', 'Arial Unicode MS Regular'],
        'text-size': isDesktop ? 12 : 11.5,
        // 'text-offset': isDesktop ? [desktopX, 0.1 + desktopY] : [mobileX, 0.1 + mobileY],
        // 'text-anchor': isDesktop ? 'left' : 'center',
        'text-offset': [
          'match',
          ['get', 'attr_IncidentName'],
          'Cherry', ['literal', (isDesktop ? [desktopX, 3.1 + desktopY] : [mobileX, 3.1 + mobileY])],
          'Kane Springs', ['literal', (isDesktop ? [desktopX, 4.1 + desktopY] : [mobileX, 4.1 + mobileY])],
          'Sawmill', ['literal', (isDesktop ? [desktopX, 4.6 + desktopY] : [mobileX, 4.6 + mobileY])],
          ['literal', (isDesktop ? [desktopX, 0.1 + desktopY] : [mobileX, 0.1 + mobileY])], // Default value: If no match
        ],
        'text-anchor': [
          'match',
          ['get', 'attr_IncidentName'],
          'Iron', 'left',
          'Tower', 'right',
          'Hastings', 'right',
          'Sawmill', isDesktop ? 'right' : 'center',
          'Kane Springs', isDesktop ? 'right' : 'right',
          'Cherry', 'right',
          'Maple Peak', 'top-left',
          'Grapevine', isDesktop ? 'left' : 'right',
          'left', // Default value: If no match
        ],
        'text-allow-overlap': true,
        // 'text-variable-anchor': ['left', 'center', 'right', 'top'],
      },
      paint: {
        'text-halo-color': '#FFFFFF', // White halo
        'text-halo-width': 1,
        'text-halo-blur': 0.5,
        'text-color': [
          'match',
          ['get', 'active'],
          'true', '#000000',
          'false', '#666666',
          '#000000', // Default value: If no match
        ],
      },
      filter: ['all',
        ['any',
          ['==', ['get', 'attr_PercentContained'], null],
          ['<', ['get', 'attr_PercentContained'], 100],
        ],
        ['>', ['get', 'attr_IncidentSize'], min_acre_threshold],
        ['==', ['get', 'attr_POOState'], 'US-UT'],
      ],
    });

    map.addLayer({
      id: 'fire-contained-layer',
      type: 'symbol',
      source: 'local-geojson',
      layout: {
        'text-field': ['concat', ['get', 'attr_PercentContained'], '% contained'], // Use the "Name" property for labels
        'text-font': ['CNN Sans Display Regular', 'Arial Unicode MS Regular'],
        'text-size': isDesktop ? 12 : 11.5,
        // 'text-offset': isDesktop ? [desktopX, 1.2 + desktopY] : [mobileX, 1.2 + mobileY],
        // 'text-anchor': isDesktop ? 'left' : 'center',
        'text-offset': [
          'match',
          ['get', 'attr_IncidentName'],
          'Cherry', ['literal', (isDesktop ? [desktopX, 4.2 + desktopY] : [mobileX, 4.2 + mobileY])],
          'Kane Springs', ['literal', (isDesktop ? [desktopX, 5.2 + desktopY] : [mobileX, 5.2 + mobileY])],
          'Sawmill', ['literal', (isDesktop ? [desktopX, 5.7 + desktopY] : [mobileX, 5.7 + mobileY])],
          ['literal', (isDesktop ? [desktopX, 1.2 + desktopY] : [mobileX, 1.2 + mobileY])], // Default value: If no match
        ],
        'text-anchor': [
          'match',
          ['get', 'attr_IncidentName'],
          'Iron', 'left',
          'Tower', 'right',
          'Hastings', 'right',
          'Kane Springs', isDesktop ? 'right' : 'right',
          'Sawmill', isDesktop ? 'right' : 'center',
          'Cherry', 'right',
          'Maple Peak', 'top-left',
          'Grapevine', isDesktop ? 'left' : 'right',
          'left', // Default value: If no match
        ],
        'text-allow-overlap': true,
      },
      paint: {
        'text-halo-color': '#FFFFFF', // White halo
        'text-halo-width': 1,
        'text-halo-blur': 0.5,
        'text-color': [
          'match',
          ['get', 'active'],
          'true', '#000000',
          'false', '#666666',
          '#000000', // Default value: If no match
        ],
      },
      filter: ['all',
        ['any',
          // ['!==', ['get', 'attr_PercentContained'], null],
          ['<', ['get', 'attr_PercentContained'], 100],
        ],
        ['>', ['get', 'attr_IncidentSize'], min_acre_threshold],
        ['==', ['get', 'attr_POOState'], 'US-UT'],
      ],
    });


    // Filter out smaller fires
    // map.setFilter('fire-label-layer', ['>', ['get', 'attr_IncidentSize'], min_acre_threshold]);
    // map.setFilter('fire-meta-layer', ['>', ['get', 'attr_IncidentSize'], min_acre_threshold]);
    // map.setFilter('fire-contained-layer', ['>', ['get', 'attr_IncidentSize'], min_acre_threshold]);
  });
};

drawMap();

let prevW = window.innerWidth;
const redraw = () => {
  if (!map || window.innerWidth === prevW) return;
  map.setMaxBounds(options.mapBounds);
  map.setMaxZoom(options.maxZoom);
  map.setMinZoom(options.minZoom);
  map.setZoom(options.initZoom);
  map.setCenter(options.center);
  map.resize();
  prevW = window.innerWidth;
};
window.addEventListener('resize', drawMap);


// fyi, disabling an empty mapbox button in graphic.scss with
// .mapboxgl-ctrl-attrib-button { display: none }
